import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Box,
  CircularProgress,
  Switch,
  Divider,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import MDButton from "components/MDButton";
import { scenarioTypes } from "services/utils";
import { getDepartments } from "services/api";

export default function AddScenarioDialog({ open, onClose, onSave, scenarios }) {
  const { t } = useTranslation();

  const [scenarioName, setScenarioName] = useState("");
  const [scenarioBudget, setScenarioBudget] = useState("");
  const [scenarioType, setScenarioType] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cloning States
  const [isCloning, setIsCloning] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);

  // Department States
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [fetchingDepts, setFetchingDepts] = useState(false);

  // Fetch departments when dialog opens
  useEffect(() => {
    if (open) {
      const fetchDepts = async () => {
        setFetchingDepts(true);
        try {
          const res = await getDepartments();
          // Add an "All Departments" option at the top
          setDepartments([
            { departmentId: 0, departmentName: t("user.scenarios.allDepartments") },
            ...res,
          ]);
        } catch (error) {
          console.error("Failed to fetch departments", error);
        } finally {
          setFetchingDepts(false);
        }
      };
      fetchDepts();
    }
  }, [open, t]);

  const cloneOptions = [
    { id: "company", scenarioName: t("user.scenarios.companyData") || "Company Data" },
    ...scenarios,
  ];

  const handleSave = async () => {
    if (scenarioName) {
      setLoading(true);

      const payload = {
        scenarioName,
        scenarioBudget: Number(scenarioBudget),
        type: scenarioType,
        isCloning: isCloning,
        sourceScenarioId: cloneSource?.id === "company" ? null : cloneSource?.id,
        fromCompany: cloneSource?.id === "company",
        // Pass departmentId if cloning from company, otherwise null
        departmentId: cloneSource?.id === "company" ? selectedDepartment?.departmentId : null,
      };

      await onSave(payload);

      // Reset form
      setScenarioName("");
      setScenarioBudget("");
      setScenarioType(null);
      setIsCloning(false);
      setCloneSource(null);
      setSelectedDepartment(null);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("user.scenarios.addScenario")}</DialogTitle>
      <DialogContent>
        {/* Clone Toggle */}
        {scenarioType !== "GAHAR Phase 1" && (
          <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="button" fontWeight="regular" color="text">
              {t("user.scenarios.cloneExistingData")}
            </Typography>
            <Switch
              checked={isCloning}
              onChange={(e) => {
                setIsCloning(e.target.checked);
                if (!e.target.checked) {
                  setCloneSource(null);
                  setSelectedDepartment(null);
                }
              }}
            />
          </Box>
        )}

        {isCloning && (
          <Box mb={3}>
            {/* 1. Select Source (Scenario or Company) */}
            <Autocomplete
              options={cloneOptions}
              getOptionLabel={(option) => option.scenarioName || ""}
              value={cloneSource}
              onChange={(_, newValue) => {
                setCloneSource(newValue);
                setSelectedDepartment(null); // Reset dept if source changes
                if (newValue && newValue.id !== "company") {
                  setScenarioName(`Clone of ${newValue.scenarioName}`);
                  setScenarioBudget(newValue.scenarioBudget || "");
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label={t("user.scenarios.selectSource")} margin="normal" />
              )}
            />

            {/* 2. Select Department (Only show if Company Data is selected) */}
            {cloneSource?.id === "company" && (
              <Autocomplete
                options={departments}
                getOptionLabel={(option) => option.departmentName || ""}
                value={selectedDepartment}
                loading={fetchingDepts}
                onChange={(_, newValue) => setSelectedDepartment(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("user.scenarios.selectDepartment")}
                    margin="normal"
                    helperText={t("user.scenarios.deptFilterHint")}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {fetchingDepts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        <TextField
          fullWidth
          margin="normal"
          label={t("user.scenarios.scenarioName") + " *"}
          value={scenarioName}
          onChange={(e) => setScenarioName(e.target.value)}
        />

        {scenarioType !== "GAHAR Phase 1" && (
          <TextField
            fullWidth
            margin="normal"
            label={t("user.scenarios.scenarioBudget")}
            value={scenarioBudget === "" ? "" : Number(scenarioBudget).toLocaleString()}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/,/g, "");
              if (rawValue === "" || (/^\d+$/.test(rawValue) && Number(rawValue) >= 0)) {
                setScenarioBudget(rawValue);
              }
            }}
          />
        )}

        <Autocomplete
          options={scenarioTypes()}
          value={scenarioType}
          onChange={(_, newValue) => {
            setScenarioType(newValue);
            setIsCloning(false);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              fullWidth
              label={t("user.scenarios.scenarioType")}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          {t("admin.areas.cancel")}
        </MDButton>
        {loading ? (
          <CircularProgress size={24} sx={{ mx: 2 }} />
        ) : (
          <MDButton
            variant="contained"
            color="info"
            onClick={handleSave}
            disabled={!scenarioName || (isCloning && !cloneSource)}
          >
            {t("admin.areas.save")}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

AddScenarioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  scenarios: PropTypes.array.isRequired,
};
